// Real links for list rows — so Ctrl/Cmd+Click, middle-click and Shift+Click open
// an item in a new tab or window the way they do on any web page. (f-02008)
//
// Only a real, hit-testable `<a href>` gets those behaviours from the browser: a
// background tab on Ctrl/Cmd+Click and on middle-click, a new window on
// Shift+Click, "Open link in new tab / Copy link address" on right-click, the URL
// in the status bar, Ctrl+Enter from the keyboard. window.open() can do none of
// that reliably (it cannot open a BACKGROUND tab), so a row never fakes it.
//
// The anchor takes over exactly one thing: a plain, unmodified primary click
// (which is also what Enter on a focused link fires). That click is
// preventDefault-ed and handed to the surface's EXISTING open function, so the
// prefetch, the chunk preload, the slide and the coarse-pointer no-slide rule all
// stay where they already were. Every other click is left to the browser.
//
// Pure, hook-free and import-free on purpose: it runs inside `.map` callbacks
// (a hook there fails the Rules-of-Hooks build gate), and RowLink, which imports
// it, is pulled into pages that entityPop.ts imports dynamically — a page or
// barrel import here would be a cycle that builds clean and crashes at runtime.

import type { MouseEvent } from 'react';

type ClickLike = Pick<MouseEvent, 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'defaultPrevented'>;

/**
 * TRUE only for a plain primary-button click with no modifier. Middle click,
 * Ctrl/Cmd, Shift, Alt — or an event a nested control already prevented — must
 * fall through to the browser. The status page has its own copy in its own
 * bundle (status/router.tsx); this is the main app's.
 */
export function isPlainLeftClick(e: ClickLike): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented;
}

/** An app path ("/backlog/1", never the protocol-relative "//host"). */
function isAppPath(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

/** An absolute http(s) URL — the only kind of external link a row may carry. */
export function isHttpUrl(href: string | null | undefined): href is string {
  return !!href && /^https?:\/\//i.test(href);
}

/** An href that is safe to put on a row: an app path or an http(s) URL. Anything
 *  else (`javascript:`, `data:`, a bare word from synced content) gets no href. */
export function isSafeHref(href: string | null | undefined): href is string {
  return !!href && (isAppPath(href) || isHttpUrl(href));
}

export interface LinkClickOptions {
  /** Where the row goes. Used VERBATIM — the router has no basename, so an app
   *  path needs no useHref(), and an absolute URL must not be turned into one. */
  href: string;
  /** The surface's existing open function, run for a plain left click on an app
   *  path instead of the browser's own navigation. */
  onOpen?: () => void;
  /** Runs for EVERY activation — plain, modified and middle click alike — before
   *  anything else. For side effects that belong to "the user opened this" no
   *  matter where it opens, such as marking a notification read. */
  onActivate?: () => void;
  /** An item outside PCP: opens in a new tab and is never intercepted. */
  external?: boolean;
}

export interface LinkClickProps {
  href: string | undefined;
  target?: '_blank';
  rel?: string;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  onAuxClick: (e: MouseEvent<HTMLElement>) => void;
}

/**
 * The props that turn an `<a>` into a list-row link. Spread them onto the row
 * itself (a row with no nested controls) or let `<RowLink>` put them on a
 * stretched overlay (a row that has some).
 *
 * An unsafe href renders as no href at all: an inert anchor beats a live
 * `javascript:` link. An absolute http(s) URL is left to the browser even on a
 * plain click — the router only understands app paths.
 */
export function linkClickProps({ href, onOpen, onActivate, external }: LinkClickOptions): LinkClickProps {
  const safe = isSafeHref(href);
  const inApp = safe && isAppPath(href);
  return {
    href: safe ? href : undefined,
    ...(external ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {}),
    onClick: (e) => {
      onActivate?.();
      if (!external && inApp && onOpen && isPlainLeftClick(e)) {
        e.preventDefault();
        onOpen();
      }
    },
    // A middle click fires `auxclick`, not `click` — the side effect must still run.
    onAuxClick: (e) => { if (e.button === 1) onActivate?.(); },
  };
}
